import React, { useState, useEffect } from 'react';
import { useToast } from '../components/ToastProvider';

interface UserProfile {
  name: string;
  email: string;
  phone: string;
  company: string;
  website: string;
  bio: string;
}

interface Questionnaire {
  id: string;
  businessName: string;
  businessType: string;
  websiteGoal: string;
  submittedAt: string;
  status: 'pending' | 'in-review' | 'completed';
}

interface PaymentHistory {
  id: string;
  package: string;
  amount: string;
  date: string;
  status: 'completed' | 'pending' | 'failed';
}

const UserProfilePage: React.FC = () => {
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<'profile' | 'questionnaires' | 'payments'>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const [profile, setProfile] = useState<UserProfile>({
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '+1 (555) 123-4567',
    company: 'Acme Corporation',
    website: 'https://acme.com',
    bio: 'Passionate entrepreneur looking to enhance our digital presence with a modern, user-friendly website.'
  });

  const [originalProfile, setOriginalProfile] = useState<UserProfile>(profile);

  // Mock data - in real app, this would come from API
  const questionnaires: Questionnaire[] = [
    {
      id: '1',
      businessName: 'Acme Corporation',
      businessType: 'Technology/Software',
      websiteGoal: 'Generate leads/inquiries',
      submittedAt: '2024-01-15',
      status: 'completed'
    },
    {
      id: '2',
      businessName: 'Acme Store',
      businessType: 'E-commerce/Retail',
      websiteGoal: 'Sell products online',
      submittedAt: '2024-02-20',
      status: 'in-review'
    }
  ];

  const paymentHistory: PaymentHistory[] = [
    {
      id: '1',
      package: 'Professional Package',
      amount: '$2,499',
      date: '2024-01-20',
      status: 'completed'
    },
    {
      id: '2',
      package: 'Starter Package',
      amount: '$999',
      date: '2024-02-25',
      status: 'pending'
    }
  ];

  useEffect(() => {
    // Simulate loading user data
    const timer = setTimeout(() => {
      addToast({
        type: 'info',
        title: 'Demo Mode',
        message: 'This is demo data. User authentication will be implemented in Phase 2.',
        duration: 4000
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [addToast]);

  const handleProfileChange = (field: keyof UserProfile, value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setOriginalProfile(profile);
      setIsEditing(false);
      
      addToast({
        type: 'success',
        title: 'Profile Updated',
        message: 'Your profile has been successfully updated.',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Update Failed',
        message: 'There was an error updating your profile. Please try again.',
      });
    }
    
    setIsLoading(false);
  };

  const handleCancelEdit = () => {
    setProfile(originalProfile);
    setIsEditing(false);
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      'in-review': 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status as keyof typeof styles]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
      </span>
    );
  };

  const renderProfileTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-serenity-dark">Profile Information</h2>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="bg-serenity-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Edit Profile
          </button>
        ) : (
          <div className="space-x-2">
            <button
              onClick={handleCancelEdit}
              className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveProfile}
              disabled={isLoading}
              className="bg-serenity-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
          {isEditing ? (
            <input
              type="text"
              value={profile.name}
              onChange={(e) => handleProfileChange('name', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-serenity-blue focus:border-transparent"
            />
          ) : (
            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg">{profile.name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
          {isEditing ? (
            <input
              type="email"
              value={profile.email}
              onChange={(e) => handleProfileChange('email', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-serenity-blue focus:border-transparent"
            />
          ) : (
            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg">{profile.email}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
          {isEditing ? (
            <input
              type="tel"
              value={profile.phone}
              onChange={(e) => handleProfileChange('phone', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-serenity-blue focus:border-transparent"
            />
          ) : (
            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg">{profile.phone}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
          {isEditing ? (
            <input
              type="text"
              value={profile.company}
              onChange={(e) => handleProfileChange('company', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-serenity-blue focus:border-transparent"
            />
          ) : (
            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg">{profile.company}</p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
          {isEditing ? (
            <input
              type="url"
              value={profile.website}
              onChange={(e) => handleProfileChange('website', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-serenity-blue focus:border-transparent"
            />
          ) : (
            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg">
              {profile.website ? (
                <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-serenity-blue hover:underline">
                  {profile.website}
                </a>
              ) : (
                'No website specified'
              )}
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
          {isEditing ? (
            <textarea
              value={profile.bio}
              onChange={(e) => handleProfileChange('bio', e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-serenity-blue focus:border-transparent"
            />
          ) : (
            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg">{profile.bio}</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderQuestionnairesTab = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-serenity-dark">Questionnaire Responses</h2>
      
      {questionnaires.length === 0 ? (
        <div className="text-center py-8">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-500 text-lg">No questionnaires submitted yet</p>
          <a href="/questionnaire" className="text-serenity-blue hover:underline">Start a new questionnaire</a>
        </div>
      ) : (
        <div className="space-y-4">
          {questionnaires.map((questionnaire) => (
            <div key={questionnaire.id} className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-serenity-dark">{questionnaire.businessName}</h3>
                  <p className="text-gray-600">{questionnaire.businessType}</p>
                </div>
                {getStatusBadge(questionnaire.status)}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Goal:</span>
                  <span className="ml-2 text-gray-600">{questionnaire.websiteGoal}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Submitted:</span>
                  <span className="ml-2 text-gray-600">{new Date(questionnaire.submittedAt).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="mt-4 flex space-x-2">
                <button className="text-serenity-blue hover:underline text-sm">View Details</button>
                <button className="text-serenity-blue hover:underline text-sm">Download PDF</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderPaymentsTab = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-serenity-dark">Payment History</h2>
      
      {paymentHistory.length === 0 ? (
        <div className="text-center py-8">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
          <p className="text-gray-500 text-lg">No payments yet</p>
          <a href="/packages" className="text-serenity-blue hover:underline">Browse our packages</a>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paymentHistory.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{payment.package}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{payment.amount}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{new Date(payment.date).toLocaleDateString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(payment.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button className="text-serenity-blue hover:underline mr-4">View Receipt</button>
                    <button className="text-serenity-blue hover:underline">Download</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-serenity-dark mb-4">
            User Profile
          </h1>
          <p className="text-xl text-gray-600">
            Manage your account, view your projects, and track your payments.
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {[
                { id: 'profile', label: 'Profile', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
                { id: 'questionnaires', label: 'Questionnaires', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
                { id: 'payments', label: 'Payments', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`group inline-flex items-center py-4 px-6 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-serenity-blue text-serenity-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } transition-colors duration-200`}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                  </svg>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-8">
            {activeTab === 'profile' && renderProfileTab()}
            {activeTab === 'questionnaires' && renderQuestionnairesTab()}
            {activeTab === 'payments' && renderPaymentsTab()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
